import {Box, Link, Text, Token, Octicon} from '@primer/react'
import {graphql, useQueryLoader, type PreloadedQuery, usePreloadedQuery, useFragment} from 'react-relay'
import type {TrackedByQuery} from './__generated__/TrackedByQuery.graphql'
import {IssueClosedIcon, IssueOpenedIcon, SkipIcon} from '@primer/octicons-react'
import {useEffect, type FC} from 'react'
import type {IssueStateReason, TrackedByFragment$key} from './__generated__/TrackedByFragment.graphql'

type TrackedByProps = {
  issueId: string
  url: string
  isSmall?: boolean
}

type TrackingIssue = {
  number: number
  url: string
  stateReason?: IssueStateReason | null
}

type TrackedByFetchedProps = {
  trackedByKey: TrackedByFragment$key
  url: string
  isSmall?: boolean
}

type TrackedByInternalProps = {
  queryRef: PreloadedQuery<TrackedByQuery>
  url: string
  isSmall?: boolean
}

export const TrackedByFragment = graphql`
  fragment TrackedByFragment on Issue {
    ... on Issue {
      trackedInIssues(first: 10) {
        nodes {
          number
          url
          stateReason
        }
        totalCount
      }
    }
  }
`

export const TrackedByGraphQLQuery = graphql`
  query TrackedByQuery($issueId: ID!) {
    node(id: $issueId) {
      ... on Issue {
        ...TrackedByFragment
      }
    }
  }
`

export const SmallTrackedBy: FC<TrackedByProps> = props => <TrackedBy {...props} isSmall={true} />

export function TrackedBy({issueId, url, isSmall}: TrackedByProps) {
  const [queryRef, loadQueryRef] = useQueryLoader<TrackedByQuery>(TrackedByGraphQLQuery)

  useEffect(() => {
    loadQueryRef({issueId}, {fetchPolicy: 'store-only'})
  }, [loadQueryRef, issueId])

  if (!queryRef) {
    return null
  }

  return <TrackedByInternal queryRef={queryRef} url={url} isSmall={isSmall} />
}

export function TrackedByInternal({queryRef, url, isSmall}: TrackedByInternalProps) {
  const data = usePreloadedQuery<TrackedByQuery>(TrackedByGraphQLQuery, queryRef)
  if (!data.node) {
    return null
  }
  return <TrackedByFetched trackedByKey={data.node} url={url} isSmall={isSmall} />
}

function TrackedByFetched({trackedByKey, url, isSmall = false}: TrackedByFetchedProps) {
  const data = useFragment(TrackedByFragment, trackedByKey)

  const numberOfTrackingIssues = data.trackedInIssues?.totalCount ?? 0
  if (numberOfTrackingIssues === 0) return null

  const trackingIssues = data.trackedInIssues?.nodes?.flatMap(a => (a ? a : [])) ?? []

  const trackingItem =
    trackingIssues.length === 1 ? (
      <SingleTrackingItem trackingIssue={trackingIssues[0]!} url={url} omitIcon={isSmall} omitHover={isSmall} />
    ) : (
      <MultipleTrackingItems trackingIssues={trackingIssues} url={url} omitHover={isSmall} />
    )

  const tokenContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      {trackingItem}
    </Box>
  )

  if (isSmall) {
    return tokenContent
  }

  return (
    <Box>
      <Token
        text={tokenContent}
        size={'xlarge'}
        sx={{
          px: 'var(--control-small-paddingInline-normal)',
          backgroundColor: 'canvas.default',
          fontWeight: '400',
          fontSize: 0,
          gap: 1,
        }}
      />
    </Box>
  )
}

function getIssueIcon(stateReason?: IssueStateReason | null) {
  if (stateReason === 'COMPLETED') {
    return {
      color: 'done.fg',
      icon: IssueClosedIcon,
    }
  } else if (stateReason === 'NOT_PLANNED') {
    return {
      color: 'fg.muted',
      icon: SkipIcon,
    }
  } else {
    return {
      color: 'open.fg',
      icon: IssueOpenedIcon,
    }
  }
}

type TrackingItemBaseProps = {
  url: string
  omitIcon?: boolean
  omitHover?: boolean
}
type SingleTrackingItemProps = {
  trackingIssue: TrackingIssue
} & TrackingItemBaseProps

const SingleTrackingItem: FC<SingleTrackingItemProps> = ({trackingIssue, url, omitIcon, omitHover}) => {
  const icon = getIssueIcon(trackingIssue.stateReason)

  return (
    <>
      <Text
        data-hovercard-url={!omitHover ? `${url}/tracked_in/hovercard/` : undefined}
        sx={{
          marginRight: '4px',
        }}
      >
        Tracked by
      </Text>
      <Box
        key={trackingIssue.number}
        data-hovercard-url={!omitHover ? `${trackingIssue.url}/hovercard` : undefined}
        sx={{display: 'flex', alignItems: 'center', gap: '2px'}}
      >
        {!omitIcon && <Octicon icon={icon.icon} size={16} sx={{color: icon.color}} />}
        <Link href={trackingIssue.url}>{`#${trackingIssue.number}`}</Link>
      </Box>
    </>
  )
}

type MultipleTrackingItemProps = {
  trackingIssues: TrackingIssue[]
} & TrackingItemBaseProps

const MultipleTrackingItems: FC<MultipleTrackingItemProps> = ({trackingIssues, url, omitHover}) => (
  <Text
    data-hovercard-url={!omitHover ? `${url}/tracked_in/hovercard/` : undefined}
  >{`Tracked by ${trackingIssues.length} issues`}</Text>
)

try{ TrackedBy.displayName ||= 'TrackedBy' } catch {}
try{ TrackedByInternal.displayName ||= 'TrackedByInternal' } catch {}
try{ TrackedByFetched.displayName ||= 'TrackedByFetched' } catch {}
try{ SingleTrackingItem.displayName ||= 'SingleTrackingItem' } catch {}
try{ MultipleTrackingItems.displayName ||= 'MultipleTrackingItems' } catch {}